import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { ThemeContext } from 'styled-components';

import {
  SiteBuilderModule,
  StyledHeading,
  StyledParagraph,
  StyledImageTag,
  StyledCallToAction,
} from '../sharedComponents';

import { layoutTypes } from './constants';
import { TicketColumn } from './innerComponents';
import {
  sectionStyles,
  Wrapper,
  ModuleHeading,
  Columns,
  CTAWrapper,
} from './index.style';

export default function TicketsModule({ section }) {
  const {
    title,
    titleSize,
    introParagraph,
    headingAlt,
    moduleHeading: { path: sectionImagePath } = {},
    link,
    columns,
    layout,
    type,
  } = section;

  const themeConfig = useContext(ThemeContext);

  const { visualStyles: vStyles, displayStyles } =
    themeConfig.siteBuilderModules.ticketsModule;
  const dStyles = displayStyles[layout];

  return (
    <SiteBuilderModule
      section={section}
      moduleTheme={{ vStyles, dStyles }}
      vStyles={sectionStyles}
    >
      <Wrapper>
        <div className="container">
          {(title || introParagraph) && (
            <div className="row">
              <div className="col-xs-12 col-sm-9">
                {title && (
                  <StyledHeading
                    content={title}
                    as={titleSize}
                    visualStyles={vStyles}
                    testId="tickets-title"
                  />
                )}
                {introParagraph && (
                  <StyledParagraph
                    content={introParagraph}
                    testId="tickets-paragraph"
                  />
                )}
              </div>
            </div>
          )}
          {(sectionImagePath || link?.shown) && (
            <ModuleHeading>
              <div className="row">
                <div className="col-xs-12 col-sm-6">
                  <StyledImageTag
                    src={sectionImagePath}
                    alt={headingAlt}
                    visualStyles={vStyles}
                    testId="tickets-image"
                  />
                </div>
                <div className="col-xs-12 col-sm-6 hidden-xs">
                  <CTAWrapper>
                    <StyledCallToAction
                      size="styled"
                      ctaConfig={link}
                      testId="tickets-cta"
                    />
                  </CTAWrapper>
                </div>
              </div>
            </ModuleHeading>
          )}
          {!!columns?.length && (
            <Columns>
              <div className="row">
                {columns.map((col, index) => {
                  const { sm } = dStyles.settings;
                  return (
                    <div
                      key={index}
                      className={`col-xs-12 col-sm-${
                        Array.isArray(sm) ? sm[index] : sm
                      }`}
                    >
                      <TicketColumn {...col} layout={layout} index={index} />
                    </div>
                  );
                })}
              </div>
            </Columns>
          )}
          {link?.shown && (
            <div className="row visible-xs">
              <div className="col-xs-12">
                <CTAWrapper>
                  <StyledCallToAction
                    size="styled"
                    ctaConfig={link}
                    testId="tickets-cta"
                    ga4={{
                      module_type: type,
                    }}
                  />
                </CTAWrapper>
              </div>
            </div>
          )}
        </div>
      </Wrapper>
    </SiteBuilderModule>
  );
}

TicketsModule.propTypes = {
  section: PropTypes.shape({
    title: PropTypes.string,
    titleSize: PropTypes.string,
    introParagraph: PropTypes.string,
    headingAlt: PropTypes.string,
    moduleHeading: PropTypes.shape({
      path: PropTypes.string,
    }),
    link: PropTypes.shape({
      shown: PropTypes.bool,
    }).isRequired,
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        textBodyLeft: PropTypes.string,
        textBodyRight: PropTypes.string,
        alt: PropTypes.string,
        image: PropTypes.shape({
          path: PropTypes.string,
        }),
      }),
    ),
    layout: PropTypes.oneOf(Object.values(layoutTypes)).isRequired,
  }).isRequired,
};
